//@ts-nocheck

###
Song V2
=======

A song has a list of sections.

Each section has:

name: string
length: beats
notes: array<Note>
settings: [{
  pan: float [-1, 1]
  pitchShift: float (semitones)
  volume: float
}, ...]

Status: Legacy, replaced by MidiSong

###

FX from ./lib/fx.civet

{ defaultPack:defaultSettings } from ./sample.civet

microsecondsPerMinute := 60000000

{ sqrt } := Math

{ Observable, Modal } from @danielx/dui

{ MidiEvent, DataStream, clamp, floatToPan } from @danielx/lib

{
  pitchToStaffNote
  staffNoteToPitch
  noteCompare
  quantize
} from ./lib/util.civet

assert := (predicate: boolean, message: string) ->
  throw new Error(message) unless predicate

Setting := (I={}, self=new Model(I)) ->
  self.attrObservable "url", "name"

  ["pan", "pitchShift", "volume"].forEach (name) ->
    self[name] = Observable(I[name])

    self[name].observe (newValue) ->
      I[name] = Number newValue

  self.toJSON = ->
    pan: I.pan
    pitchShift: I.pitchShift
    volume: I.volume

  return self

Model from ./lib/model.civet
Pattern from ./lib/pattern.civet
Section from ./lib/section.civet

OldSong from ./lib/legacy/song.civet

oldInstrumentMapping :=
  0: 0
  1: 1
  2: 2
  3: 4
  4: 5
  5: 8
  6: 9
  7: 11
  8: 16
  9: 17

// The properties to load from saved data files
persistentProps := ["authorId", "author", "description", "loop", "title"]

export Song := (I={}, self=new Model(I)) ->
  defaults I,
    title: "Untitled"
    description: ""
    sections: [{
      name: "Section 1"
      length: 64
      tempo: 120
      notes: []
    }]
    loop: true
    version: 2

  I.settings ?= JSON.parse JSON.stringify defaultSettings

  self.attrObservable persistentProps...
  self.attrModels "sections", Section
  self.attrModels "settings", Setting

  // Note -> section lookup
  sectionFor .= null

  self.extend
    // Clear entire song, keeping length, tempo, FX from first section, removing
    // all other sections
    clear: ->
      section := self.sections()[0]
      section.notes []

      self.sections [ section ]

    loadSettings: (samples) ->
      self.settings samples.map (sample) ->
        Setting sample

    // Patterns and ranges get distributed to the underlying sections
    addPattern: ([time, note], pattern) ->
      pattern.notes().forEach ([t, n, rest...]) ->
        self.addNote [time + t, note + n, rest...]

    // Copy notes within a range to a Pattern
    // normalize the patten so that the first note is 0,0
    copyRange: (range) ->
      t := range[0][0]
      dt := range[0][1] - t

      notes .= self.notesWithinRange(range)

      first := notes[0]
      if first
        // normalize to first note
        [t0, n0] := first
        notes = notes.map ([t, n, rest...]) ->
          [t - t0, n - n0, rest...]

      Pattern
        beats: dt
        notes: notes

    deleteRange: (range) ->
      time .= 0
      self.sections.forEach (section) ->
        l := section.length()
        section.deleteRange([[range[0][0] - time, range[0][1] - time], range[1]])
        time += l

    ###
    Pass all the notes that are upcoming to a handler fn
    The handler fn receives:

    note - a stable reference to a note that can be used as a key to an element
    section - a reference to the section object the note appears in
    s - seconds after beat t, accounts for different section bpm

    i.e. notes that occur within the interval [t, t+dt)

    t and dt are in beats

    doesn't return notes outside of [0, length)

    current is expected to be <= t

    if current is < 0 then it is considered to wrap from the end

    current is an optional reference time to receive the accumulated seconds
    relative to. This is used in the audio playback.
    ###
    upcomingNotes: (t: number, dt, fn, current=t) ->
      accumulatedBeats .= 0
      accumulatedTime .= 0
      songLength := self.length()
      songDuration := self.duration()

      if songLength <= 0 or isNaN(songLength)
        return

      if t >= songLength
        return

      // Get current up to zero and accumulate time from wrapping
      while current < 0
        accumulatedTime += songDuration
        current += songLength

      // Remove any overshoot and match the zero to the begining of the song
      if current > 0
        self.sections().forEach (section) ->
          sectionLength := section.length()
          sectionDuration := section.duration()
          if sectionLength < current
            current -= sectionLength
            accumulatedTime -= sectionDuration
          else if current > 0
            accumulatedTime -= (current / sectionLength) * sectionDuration
            current = 0

      // Walk through the sections accumulating beats and time, passing notes
      // within the target time-slice to the handler with their section and
      // seconds since reference time.
      self.sections().forEach (section) ->
        sectionLength := section.length()
        sectionDuration := section.duration()

        section.upcomingNotes t - accumulatedBeats, dt, (note) ->
          beat := note[0]
          s := (beat / sectionLength) * sectionDuration + accumulatedTime

          fn(note, section, s)

        accumulatedBeats += sectionLength
        accumulatedTime += sectionDuration

    // Return an array containing the notes within a time slice
    notesWithin: (t, dt) ->
      notes := []
      self.upcomingNotes t, dt, (note) ->
        notes.push note
      return notes

    notesWithinRange: (range) ->
      t := range[0][0]
      dt := range[0][1] - t
      [n0, n1] := range[1]

      notes := self.notesWithin(t, dt + 0.0001).filter( ([_, n]) ->
        n0 <= n <= n1
      ).sort noteCompare

    // returns [section, beatsLeft]
    // t and time in beats
    sectionAt: (t) ->
      time .= 0
      i .= 0
      sections := self.sections()

      while section := sections[i]
        time += section.length()

        if t < time
          return [section, time - t]

        i++

      return []

    addNote: (note) ->
      time .= 0
      t := note[0]
      self.sections.forEach (section) ->
        l := section.length()
        if 0 <= t - time < l
          note[0] -= time
          section.addNote(note)
          // Keep the section lookup cache up to date
          sectionFor.set(note, section)
        time += l

    removeNote: (note, nearby) ->
      time .= 0
      t := note[0]
      removed .= false
      self.sections.forEach (section) ->
        l := section.length()
        if 0 <= t - time < l
          note[0] -= time
          removed = section.removeNote(note, nearby)
        time += l

      return removed

    // return beat at which a section begins
    sectionBeginsAt: (s) ->
      t .= 0
      ret .= undefined

      self.sections.forEach (section) ->
        if section is s
          ret = t
        t += s.length()

      return ret

    // seconds
    duration: ->
      self.sections.reduce (total, section) ->
        total + section.duration()
      , 0

    // seconds
    // Sum of all section durations, but the last section crops all empty measures
    exportDuration: ->
      last := self.sections().length - 1

      self.sections.reduce (total, section, i) ->
        if i is last
          total + section.nonEmptyDuration()
        else
          total + section.duration()
      , 0

    // Length is the sum of the length of all the sections
    length: ->
      self.sections().reduce (count, section) ->
        count + section.length()
      , 0

    // Reset cached note -> section lookup
    reset: ->
      sectionFor = new Map

      self.upcomingNotes 0, self.length(), (note, section) ->
        sectionFor.set(note, section)

    // When moving a selection of notes we need to 'resection' any that fall
    // across section boundaries
    resection: (notes) ->
      notes.forEach (note) ->
        section := sectionFor.get(note)

        [t] := note
        if (t < 0) or (t >= section.length())
          // Remove from old section
          // add at proper time, inserting into new section
          sectionStart := self.sectionBeginsAt(section)

          // Note can float negative in first section
          if sectionStart is 0 and t < 0
            return

          if section.removeNoteByReference(note)
            note[0] += sectionStart
            self.addNote(note)

    toJSON: ->
      Object.assign {}, I, {
        settings: self.settings.map (s) -> s.toJSON()
      }

    toMidi: ->
      // TODO: dynamic sizing to handle larger tracks and files
      buffer := new ArrayBuffer 512 * 1024
      dataStream := new DataStream buffer

      // Ascii is a subset of utf-8 but there's currently no check to ensure that
      // only an ascii subset is used
      utf8Encoder := new TextEncoder('utf-8')

      droppedNotes .= 0

      trackCount := 1
      ticksPerBeat := 96

      // Write header
      dataStream.putAscii("MThd")
      dataStream.putUint32(6) // 6 length header
      dataStream.putUint16(0) // Format Type 0 (single track)
      dataStream.putUint16(trackCount)
      dataStream.putUint16(ticksPerBeat)

      trackBuffer := new ArrayBuffer 128 * 1024
      trackData := new DataStream trackBuffer

      channelCount .= 0
      instrumentMap := new Map

      currentTick .= 0
      deltaTime .= 0
      // Note on/off data buffer
      data := new Uint8Array 3

      settings := I.settings or defaultSettings
      sections := self.sections()
      sectionBeats .= 0

      afxPreset .= sections[0].presetName()
      afxPreset = FX.mapping[afxPreset] or afxPreset

      // dxpc synth select message (classic synth)
      trackData.putVarUint(0)
      playerMeta :=
        dxpc: 1
        synth: "legacy"
        loop: I.loop
        afxPresetName: afxPreset

      textMeta := `${JSON.stringify(playerMeta)}`
      trackData.putBytes new Uint8Array [0xff, 0x01, textMeta.length]
      trackData.putAscii textMeta

      sections.forEach (section, i) ->
        sectionLength := section.length()

        // Convert tick to deltaTime (depends on events being sorted by beat)
        tick := sectionBeats * ticksPerBeat
        if tick >= currentTick
          deltaTime = tick - currentTick
          currentTick = tick
        else
          throw new Error "Negative deltaTime"

        if sections.length > 1
          // marker event
          trackData.putVarUint(deltaTime)
          trackData.putBytes new Uint8Array [0xff, 0x06]
          bytes := utf8Encoder.encode section.name()
          trackData.putVarUint bytes.length
          trackData.putBytes bytes
          deltaTime = 0

        trackData.putVarUint(deltaTime)
        trackData.putBytes(new Uint8Array [0xff, 0x51, 3])
        trackData.putUint24(microsecondsPerMinute / section.tempo())
        deltaTime = 0

        // time signature event
        numerator := section.numerator()
        trackData.putVarUint(0)
        trackData.putBytes(new Uint8Array [0xff, 0x58, 4, numerator, 2, 24, 8])

        keySignature := section.keySignature()
        // write key signature event
        trackData.putVarUint(0)
        trackData.putBytes(new Uint8Array [0xff, 0x59, 2, keySignature, 0])

        sortedNotes := section.notes().filter (event) ->
          [beat] := event
          // drop events outside of section length
          0 <= beat <= sectionLength
        .sort(noteCompare)

        // Prepare instrument channels
        sortedNotes.forEach (event) ->
          [beat, staffNote, accidental, instrument] := event

          // instrument -> channel mapping (error if more than 16 instruments)
          // TODO: per section?
          c .= instrumentMap.get instrument
          if !c?
            if channelCount <= 15
              c = channelCount++
              instrumentMap.set instrument, c

        fillRemainingInstruments(instrumentMap)

        instrumentMap.forEach (channelId, instrumentId) ->
          {pan:defaultPan} := defaultSettings[instrumentId]
          {pan, pitchShift, volume} := settings[instrumentId]
          assert channelId < 16, "Invalid channel id"

          // Use default bank instead of drum bank
          if channelId is 9
            trackData.putVarUint(0)
            trackData.putUint8 0xb0 + channelId // CC
            trackData.putUint8 0x00 // 0x00 Bank Select
            trackData.putUint8 0x00 // Bank 0

          // program change events
          trackData.putVarUint(0)
          trackData.putUint8 0xc0 + channelId
          trackData.putUint8 0x7f & instrumentId

          // Channel Volume
          trackData.putVarUint(0)
          trackData.putUint8 0xb0 + channelId
          trackData.putUint8 0x07 // volume
          trackData.putUint8 clamp sqrt(volume) * 100, 0, 127

          // Channel Panning
          trackData.putVarUint(0)
          trackData.putUint8 0xb0 + channelId
          trackData.putUint8 0x0a // pan
          trackData.putUint8 floatToPan(pan - defaultPan)

          // NOTE: skipping tuning for now because RPN is more complicated

        // Need to collect and sort the events again because we're adding note
        // off events. If the delta time is zero it gets weird on the soundfont.
        noteEvents := []

        sortedNotes.forEach (event) ->
          [beat, staffNote, accidental, instrument] := event

          // Convert tick to deltaTime (depends on events being sorted by beat)
          tick := (sectionBeats + beat) * ticksPerBeat

          // channel
          c := instrumentMap.get instrument
          if c?
            // note + accidental -> midi pitch
            n := 60 + staffNoteToPitch(staffNote, accidental, keySignature)

            // Note On
            noteEvents.push new MidiEvent tick, new Uint8Array [
              0x90 + c // note on + channel #
              n & 0x7f // note
              0x7f // velocity
            ]
            // Note Off (uses zero velocity noteOn)
            noteEvents.push new MidiEvent tick + 1, new Uint8Array [
              0x90 + c // note on + channel #
              n & 0x7f // note
              0x00 // velocity
            ]
          else
            droppedNotes++

        noteEvents.sort (a, b) ->
          a.tick - b.tick

        noteEvents.forEach ({tick, data}) ->
          if tick >= currentTick
            deltaTime = tick - currentTick
            currentTick = tick
          else
            throw new Error "Negative deltaTime"

          trackData.putVarUint(deltaTime)
          trackData.putBytes(data)

        // Add sectionLength to deltaTime to carry over to next section
        sectionBeats += sectionLength

      if droppedNotes
        setTimeout ->
          Modal.alert ```
            This song had more than 16 instruments.
            ${droppedNotes} notes were dropped.
          ```

      tick := sectionBeats * ticksPerBeat
      if tick >= currentTick
        deltaTime = tick - currentTick
        currentTick = tick
      else
        throw new Error "Negative deltaTime"

      // Write end of track message
      trackData.putVarUint(deltaTime)
      trackData.putBytes [0xff, 0x2f, 0]

      // Write track
      dataStream.putAscii("MTrk")
      dataStream.putUint32(trackData.position)
      dataStream.putBytes(trackData.bytes())

      return dataStream.bytes()

    fromJSON: (data) ->
      // Load old style if necessary
      if data.patterns or data.notes
        song := OldSong().fromJSON(data)
        notes := song.upcomingNotes(0, song.size())
        newNotes := notes.map ([beat, note, instrument]) ->
          [beat, pitchToStaffNote(note)..., oldInstrumentMapping[instrument]]

        section := self.sections()[0]

        section.notes newNotes
        lastTime := newNotes.reduce (max, [t]) ->
          Math.max(t, max)
        , 0
        section.length quantize(lastTime + 1.99999, 4)
        section.tempo song.tempo()

      else
        self.sections data.sections.map (s) -> Section s
        persistentProps.forEach (prop) ->
          self[prop] data[prop]

        if data.settings
          data.settings.forEach (s, i) ->
            setting := self.settings()[i]

            Object.keys(s).forEach (key) ->
              setting[key]? s[key]

        // Convert old F-pitched notes
        if data.version is undefined
          self.sections.forEach (section) ->
            notes := section.I.notes
            notes.forEach (n, i) ->
              [beat, staffNote, accidental, instrument] .= notes[i]
              pitch := staffNoteToPitch(staffNote, accidental) + 5
              [staffNote, accidental] = pitchToStaffNote(pitch)
              notes[i][1] = staffNote
              notes[i][2] = accidental

      self.reset()

      return self

  self.reset()

  return self

// Fill in any missing instrument channels so we always end up with 16
// this maps instrument -> channel
fillRemainingInstruments := (instrumentMap) ->
  // Fill out remaining instruments
  i .= 0
  c .= 0
  values := Array.from(instrumentMap.values())

  while i < 16
    if !instrumentMap.has(i)
      while c < 16
        if values.includes(c)
          c++
        else
          break
      if c < 16
        instrumentMap.set(i, c++)
    i++

export default Song
